// Canonical region list matching the REAL backend region enum (confirmed via
// ListingDetail.tsx's regionMap and live API data). This intentionally does NOT reuse mockData.ts's
// REGIONS array, which nests Bishkek as a district under a "Чүй" region — that
// doesn't match the backend, where BISHKEK and CHUI are separate top-level
// values. Filtering by region=CHUI will not surface Bishkek listings.
export type BackendRegion =
  | 'BISHKEK'
  | 'CHUI'
  | 'OSH'
  | 'JALAL_ABAD'
  | 'YSSYK_KOL'
  | 'NARYN'
  | 'TALAS'
  | 'BATKEN';

export type BackendCategory = 'APARTMENT' | 'HOUSE' | 'PLOT' | 'COMMERCIAL';

// Matches the app's i18next resource keys exactly (src/i18n/index.ts) — note
// Kyrgyz is 'kg' internally even though its hreflang/URL-prefix is 'ky'
// (the ISO code search engines expect).
export type LangCode = 'ru' | 'kg' | 'kz' | 'uz' | 'tr' | 'en' | 'zh';

export interface LangDef {
  code: LangCode;
  /** URL prefix segment and hreflang value; empty string = default, unprefixed (ru). */
  urlPrefix: string;
  hreflang: string;
}

// 'ru' is the default/unprefixed language — matches the primary keywords this
// SEO push targets ("купить квартиру Бишкек" etc). Every other language the
// site already supports gets its own prefixed, hreflang-linked URL tree.
// ru/ky are region-qualified (ru-KG, ky-KG) per the SEO brief — bare "ru"
// would target Russian speakers everywhere and compete with russia-wide
// listing sites for that generic signal, instead of the actually-local
// audience this content serves.
export const SEO_LANGS: LangDef[] = [
  { code: 'ru', urlPrefix: '', hreflang: 'ru-KG' },
  { code: 'kg', urlPrefix: 'ky', hreflang: 'ky-KG' },
  { code: 'kz', urlPrefix: 'kz', hreflang: 'kk' },
  { code: 'uz', urlPrefix: 'uz', hreflang: 'uz' },
  { code: 'tr', urlPrefix: 'tr', hreflang: 'tr' },
  { code: 'en', urlPrefix: 'en', hreflang: 'en' },
  { code: 'zh', urlPrefix: 'zh', hreflang: 'zh' },
];

export function langFromPathname(pathname: string): LangDef {
  const firstSegment = pathname.split('/').filter(Boolean)[0];
  return (
    SEO_LANGS.find((l) => l.urlPrefix && l.urlPrefix === firstSegment) ??
    SEO_LANGS[0]
  );
}

type Names = Record<LangCode, string>;

export interface RegionDef {
  slug: string;
  value: BackendRegion;
  names: Names;
}

export const SEO_REGIONS: RegionDef[] = [
  {
    slug: 'bishkek',
    value: 'BISHKEK',
    names: { ru: 'Бишкек', kg: 'Бишкек', kz: 'Бішкек', uz: 'Bishkek', tr: 'Bişkek', en: 'Bishkek', zh: '比什凯克' },
  },
  {
    slug: 'chuy',
    value: 'CHUI',
    names: {
      ru: 'Чуйская область',
      kg: 'Чүй облусу',
      kz: 'Шу облысы',
      uz: "Chuy viloyati",
      tr: 'Çüy Bölgesi',
      en: 'Chuy Region',
      zh: '楚河州',
    },
  },
  {
    slug: 'osh',
    value: 'OSH',
    names: { ru: 'Ош', kg: 'Ош', kz: 'Ош', uz: 'Osh', tr: 'Oş', en: 'Osh', zh: '奥什' },
  },
  {
    slug: 'jalal-abad',
    value: 'JALAL_ABAD',
    names: {
      ru: 'Джалал-Абад',
      kg: 'Жалал-Абад',
      kz: 'Жалал-Абад',
      uz: 'Jalol-Obod',
      tr: 'Celalabat',
      en: 'Jalal-Abad',
      zh: '贾拉拉巴德',
    },
  },
  {
    slug: 'yssyk-kul',
    value: 'YSSYK_KOL',
    names: {
      ru: 'Иссык-Куль',
      kg: 'Ысык-Көл',
      kz: 'Ыстықкөл',
      uz: 'Issiqkoʻl',
      tr: 'Isık Göl',
      en: 'Issyk-Kul',
      zh: '伊塞克湖',
    },
  },
  {
    slug: 'naryn',
    value: 'NARYN',
    names: { ru: 'Нарын', kg: 'Нарын', kz: 'Нарын', uz: 'Norin', tr: 'Narın', en: 'Naryn', zh: '納倫' },
  },
  {
    slug: 'talas',
    value: 'TALAS',
    names: { ru: 'Талас', kg: 'Талас', kz: 'Талас', uz: 'Talas', tr: 'Talas', en: 'Talas', zh: '塔拉斯' },
  },
  {
    slug: 'batken',
    value: 'BATKEN',
    names: { ru: 'Баткен', kg: 'Баткен', kz: 'Баткен', uz: 'Botken', tr: 'Batken', en: 'Batken', zh: '巴特肯' },
  },
];

export interface CategoryDef {
  slug: string;
  value: BackendCategory;
  /** Singular, e.g. for "1-комнатная квартира" */
  names: Names;
  /** Plural/heading form, e.g. "Квартиры" */
  namesPlural: Names;
}

export const SEO_CATEGORIES: CategoryDef[] = [
  {
    slug: 'kvartiry',
    value: 'APARTMENT',
    names: { ru: 'Квартира', kg: 'Батир', kz: 'Пәтер', uz: 'Kvartira', tr: 'Daire', en: 'Apartment', zh: '公寓' },
    namesPlural: {
      ru: 'Квартиры',
      kg: 'Батирлер',
      kz: 'Пәтерлер',
      uz: 'Kvartiralar',
      tr: 'Daireler',
      en: 'Apartments',
      zh: '公寓',
    },
  },
  {
    slug: 'doma',
    value: 'HOUSE',
    names: { ru: 'Дом', kg: 'Үй', kz: 'Үй', uz: 'Uy', tr: 'Ev', en: 'House', zh: '房屋' },
    namesPlural: { ru: 'Дома', kg: 'Үйлөр', kz: 'Үйлер', uz: 'Uylar', tr: 'Evler', en: 'Houses', zh: '房屋' },
  },
  {
    slug: 'uchastki',
    value: 'PLOT',
    names: {
      ru: 'Участок',
      kg: 'Жер тилкеси',
      kz: 'Жер учаскесі',
      uz: 'Yer uchastkasi',
      tr: 'Arsa',
      en: 'Land plot',
      zh: '地块',
    },
    namesPlural: {
      ru: 'Участки',
      kg: 'Жер тилкелери',
      kz: 'Жер учаскелері',
      uz: 'Yer uchastkalari',
      tr: 'Arsalar',
      en: 'Land plots',
      zh: '地块',
    },
  },
  {
    slug: 'kommercheskaya',
    value: 'COMMERCIAL',
    names: {
      ru: 'Коммерческая недвижимость',
      kg: 'Коммерциялык мүлк',
      kz: 'Коммерциялық жылжымайтын мүлік',
      uz: 'Tijorat koʻchmas mulki',
      tr: 'Ticari gayrimenkul',
      en: 'Commercial property',
      zh: '商业地产',
    },
    namesPlural: {
      ru: 'Коммерческая недвижимость',
      kg: 'Коммерциялык мүлк',
      kz: 'Коммерциялық жылжымайтын мүлік',
      uz: 'Tijorat koʻchmas mulki',
      tr: 'Ticari gayrimenkuller',
      en: 'Commercial properties',
      zh: '商业地产',
    },
  },
];

export interface RoomsDef {
  slug: string;
  minRooms?: number;
  maxRooms?: number;
  /** "1-комнатная" style adjective per language, used in titles/H1. */
  adjective: Names;
}

// Rooms sub-pages only make sense for apartments (kvartiry). Only built for
// ru/ky at page-content level for now — see findRoomsBySlug usage sites.
export const SEO_ROOMS: RoomsDef[] = [
  {
    slug: 'studiya',
    minRooms: 0,
    maxRooms: 0,
    adjective: { ru: 'Студия', kg: 'Студия', kz: 'Студия', uz: 'Studiya', tr: 'Stüdyo', en: 'Studio', zh: '开间' },
  },
  {
    slug: '1-komnatnye',
    minRooms: 1,
    maxRooms: 1,
    adjective: {
      ru: '1-комнатная',
      kg: '1 бөлмөлүү',
      kz: '1 бөлмелі',
      uz: '1 xonali',
      tr: '1+0',
      en: '1-room',
      zh: '一居室',
    },
  },
  {
    slug: '2-komnatnye',
    minRooms: 2,
    maxRooms: 2,
    adjective: {
      ru: '2-комнатная',
      kg: '2 бөлмөлүү',
      kz: '2 бөлмелі',
      uz: '2 xonali',
      tr: '1+1',
      en: '2-room',
      zh: '两居室',
    },
  },
  {
    slug: '3-komnatnye',
    minRooms: 3,
    maxRooms: 3,
    adjective: {
      ru: '3-комнатная',
      kg: '3 бөлмөлүү',
      kz: '3 бөлмелі',
      uz: '3 xonali',
      tr: '2+1',
      en: '3-room',
      zh: '三居室',
    },
  },
  {
    slug: '4-komnatnye-i-bolshe',
    minRooms: 4,
    adjective: {
      ru: '4-комнатная и больше',
      kg: '4 бөлмөдөн köп',
      kz: '4 бөлмеден көп',
      uz: "4 xona va undan ko'p",
      tr: '3+1 ve üzeri',
      en: '4+ room',
      zh: '四居室及以上',
    },
  },
];

export function findRegionBySlug(slug: string | undefined): RegionDef | undefined {
  return SEO_REGIONS.find((r) => r.slug === slug);
}

export function findCategoryBySlug(slug: string | undefined): CategoryDef | undefined {
  return SEO_CATEGORIES.find((c) => c.slug === slug);
}

export function findRoomsBySlug(slug: string | undefined): RoomsDef | undefined {
  return SEO_ROOMS.find((r) => r.slug === slug);
}

export function hubPath(
  regionSlug: string,
  categorySlug?: string,
  roomsSlug?: string,
  langPrefix?: string,
): string {
  let path = `/nedvizhimost/${regionSlug}`;
  if (categorySlug) path += `/${categorySlug}`;
  if (roomsSlug) path += `/${roomsSlug}`;
  return langPrefix ? `/${langPrefix}${path}` : path;
}
